import { useEffect, useLayoutEffect } from 'react';
import { refreshMeasurementPaths } from '@/lib/measurementPaths';
import { useLocation } from 'react-router-dom';
import { syncMeasurementConsent, trackPage, trackEvent } from '@/lib/measurement';
import { syncMarketingConsent, installMarketingNavigationBoundary } from '@/lib/marketingConsent';
import { fireAdsConversion, ADS_CONVERSIONS, initializeGoogleAds } from '@/lib/googleAdsConversions';

export function useMeasurement() {
  const { pathname, search } = useLocation();
  useLayoutEffect(() => {
    syncMeasurementConsent();
    initializeGoogleAds();
    trackPage();
  }, [pathname, search]);
  useEffect(() => {
    // The sitemap refresh only widens the page allowlist for later events.
    // It waits for the window load event and then for an idle period, because
    // firing it alongside the route's first chunks put a /sitemap.xml request
    // in the critical path and slowed the sections the visitor is waiting for.
    let active = true;
    const run = () => refreshMeasurementPaths().then(() => { if (active) { syncMeasurementConsent(); initializeGoogleAds(); trackPage(); } });
    const idle = typeof window.requestIdleCallback === 'function';
    let handle = null;
    const schedule = () => {
      handle = idle ? window.requestIdleCallback(run, { timeout: 10000 }) : setTimeout(run, 6000);
    };
    const onLoad = () => schedule();
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', onLoad, { once: true });
    return () => {
      active = false;
      window.removeEventListener('load', onLoad);
      if (handle === null) return;
      if (idle) window.cancelIdleCallback(handle); else clearTimeout(handle);
    };
  }, [pathname, search]);
  useEffect(() => {
    let frame;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => syncMarketingConsent());
    };
    // Lazy content can become eligible after the route effect has already run.
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    schedule();
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [pathname, search]);
  useEffect(() => {
    const onConsent = () => { syncMeasurementConsent(); initializeGoogleAds(); trackPage(); syncMarketingConsent(); };
    const removeBoundary = installMarketingNavigationBoundary();
    const onClick = (event) => {
      if (event.defaultPrevented || (event.button !== undefined && event.button !== 0)) return;
      const link = event.target?.closest?.('a[href]');
      if (link?.getAttribute('href')?.startsWith('tel:')) {
        trackEvent('phone_click');
        fireAdsConversion(ADS_CONVERSIONS.phone_call);
      }
    };
    window.addEventListener('tda-consent-change', onConsent);
    document.addEventListener('click', onClick);
    return () => {
      removeBoundary();
      window.removeEventListener('tda-consent-change', onConsent);
      document.removeEventListener('click', onClick);
    };
  }, []);
}